import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import {
  UniBadgeComponent,
  UniRailComponent,
  UniRailItemComponent,
  UniShellComponent,
  UniSubnavComponent,
  UniSubnavItemComponent,
  UniToasterComponent,
} from '@uniproject-git/ui-angular';
import { IconComponent } from '@uniit/ui-kit';
import { SessionGateComponent } from './core/auth/session-gate.component';
import { APP_CONFIG } from './core/config/app-config.value';
import { ROLES, type RoleId } from './core/models/role.model';
import {
  SECTION_ICONS,
  groupBySection,
  navFor,
  navLabel,
  shouldUseRail,
  type NavEntry,
  type NavSection,
} from './core/navigation';
import { DevProfileStore } from './core/state/dev-profile.store';
import { SessionStore } from './core/state/session.store';
import { DirectoryApiService } from './features/directory/api/directory-api.service';
import { InventoryApiService } from './features/inventory/api/inventory-api.service';
import { PurchaseRequestApiService } from './features/purchasing/api/purchase-request-api.service';
import { NotificationsApiService } from './features/shared/api/notifications-api.service';

@Component({
  selector: 'uniit-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(keydown.escape)': 'closeMenus()',
  },
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    IconComponent,
    SessionGateComponent,
    UniBadgeComponent,
    UniShellComponent,
    UniRailComponent,
    UniRailItemComponent,
    UniSubnavComponent,
    UniSubnavItemComponent,
    UniToasterComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected readonly session = inject(SessionStore);
  protected readonly dev = inject(DevProfileStore);
  private readonly inventory = inject(InventoryApiService);
  private readonly directory = inject(DirectoryApiService);
  private readonly purchaseRequests = inject(PurchaseRequestApiService);
  private readonly notifications = inject(NotificationsApiService);
  private readonly router = inject(Router);

  protected readonly roles = ROLES;

  /**
   * Repli du panneau, lié en deux sens à `<uni-subnav>` : c'est le kit qui rend l'en-tête et le
   * chevron, et qui écrit dans ce signal. On ne garde donc que l'état, plus le geste.
   */
  protected readonly railCollapsed = signal(App.isCompact());

  /**
   * Écran étroit (téléphone). Le kit ne prévoit aucun comportement mobile pour son shell : rail de
   * 64 px et panneau de 224 px restaient côte à côte et ne laissaient au contenu qu'une bande d'une
   * cinquantaine de pixels. Sous ce seuil, le panneau devient un tiroir superposé au contenu,
   * fermé par défaut, ouvert depuis le rail et refermé après chaque navigation. La valeur doit
   * rester alignée sur la media query de `app.scss`.
   */
  private static readonly COMPACT_QUERY = '(max-width: 768px)';

  protected readonly compact = signal(App.isCompact());

  private static isCompact(): boolean {
    return typeof matchMedia === 'function' && matchMedia(App.COMPACT_QUERY).matches;
  }

  protected toggleNav(): void {
    this.railCollapsed.update((collapsed) => !collapsed);
  }

  constructor() {
    if (typeof matchMedia !== 'function') return;

    const query = matchMedia(App.COMPACT_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      this.compact.set(event.matches);
      // Passer en écran étroit referme le tiroir ; revenir en large rouvre le panneau.
      this.railCollapsed.set(event.matches);
    };
    query.addEventListener('change', onChange);
    inject(DestroyRef).onDestroy(() => query.removeEventListener('change', onChange));

    // Sur téléphone, le tiroir se referme une fois la destination choisie.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.compact()) this.railCollapsed.set(true);
      });
  }

  // Menu de compte ouvert depuis l'avatar du rail (identité seule : nom + rôle), façon UniApp.
  private readonly accountEl = viewChild<ElementRef<HTMLElement>>('account');
  protected readonly accountOpen = signal(false);

  /**
   * Le bandeau « données de démonstration » n'a de sens que si les écrans affichent le jeu
   * de la maquette. En mode branché, il mentirait : l'inventaire vient de la vraie base.
   */
  protected readonly demoMode = APP_CONFIG.dataSource === 'demo';

  // Navigation pilotée par les permissions en mode branché (L3-04), par le rôle en démonstration :
  // c'est `navEntries` qui tranche selon la source de données.
  protected readonly nav = computed(() =>
    this.session.navEntries().map((entry) => ({
      ...entry,
      label: navLabel(entry, this.session.role()),
      count: this.countFor(entry.id),
    })),
  );

  /**
   * Les mêmes entrées, réparties par section. Chaque groupe donne une tuile de rail, et le panneau
   * ne rend que celui de la section ouverte.
   */
  protected readonly navGroups = computed(() => groupBySection(this.nav()));

  protected readonly sectionIcons = SECTION_ICONS;

  /**
   * Le rail porte-t-il les sections ? Seulement si le menu du rôle est assez long pour en tirer
   * parti — sinon les entrées vont toutes dans le panneau, en liste plate.
   *
   * Le groupement reste calculé dans les deux cas : il fixe aussi l'ORDRE des entrées, qui doit
   * rester le même d'un rôle à l'autre.
   */
  protected readonly useRail = computed(() => shouldUseRail(this.nav(), this.session.role()));

  /**
   * Chemin de l'entrée de menu dont relève l'écran courant.
   *
   * La source est la même que celle du garde d'accès : le `data.navPath` de la route quand elle
   * en déclare un (les écrans de détail, qui n'ont pas d'entrée propre), sinon l'URL sans ses
   * query params. La correspondance avec le menu est ensuite <b>exacte</b> — un préfixe laissait
   * « Mes demandes » (`/demandes`) allumé en même temps que « Qualification IT »
   * (`/demandes/qualification`), deux entrées actives pour un seul écran.
   */
  private readonly activeEntryPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.resolveEntryPath()),
    ),
    { initialValue: this.resolveEntryPath() },
  );

  private resolveEntryPath(): string {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild !== null) {
      route = route.firstChild;
    }

    const navPath = route.data['navPath'] as string | undefined;

    return navPath ?? this.router.url.split('?')[0].split('#')[0];
  }

  /**
   * L'entrée de menu active — <b>au plus une</b>.
   *
   * Deux règles, dans cet ordre, qui reprennent le raisonnement de #64 en le portant sur le shell
   * du kit :
   * <list type="number">
   * <item>égalité stricte du chemin — c'est ce qui empêchait « Mes demandes » (`/demandes`) de
   *   rester allumée sous « Qualification IT » (`/demandes/qualification`) ;</item>
   * <item>à défaut, la plus longue entrée <b>non exacte</b> qui préfixe le chemin, pour les écrans
   *   de détail qui n'ont pas d'entrée propre.</item>
   * </list>
   *
   * Le drapeau `exact` vient de `navigation.ts`, où il est <b>dérivé</b> de la liste des entrées
   * (#64) : ajouter une entrée fille suffit à rendre sa mère exacte, sans rien saisir ici. Et la
   * limite que #64 signalait — une route fille sans entrée de menu sous une entrée exacte —
   * est couverte par le `navPath` que ces routes déclarent déjà pour le garde d'accès.
   */
  protected readonly activeEntry = computed<NavEntry | undefined>(() => {
    // Le chemin est comparé SANS ses query params (`resolveEntryPath` les retire) : sans cela
    // `/commandes?commande=<id>`, où le catalogue renvoie après soumission, cesserait de
    // correspondre à l'entrée « Commandes » et le menu s'éteindrait (mise en garde de #64).
    const path = this.activeEntryPath();
    const entries = this.nav();

    return (
      entries.find((entry) => entry.path === path) ??
      [...entries]
        .filter((entry) => !entry.exact && path.startsWith(`${entry.path}/`))
        .sort((left, right) => right.path.length - left.path.length)[0]
    );
  });

  /**
   * Section ouverte. <b>Déduite de la route</b>, jamais d'un état propre au rail : une sélection
   * mémorisée à part finirait par désigner une section que l'écran affiché ne concerne pas — au
   * retour arrière du navigateur, par exemple, où personne ne clique sur le rail.
   *
   * `undefined` quand l'écran n'a pas d'entrée de menu (les notifications) : aucune tuile n'est
   * alors marquée active — la cloche l'est, et c'est elle qui dit où l'on est.
   */
  protected readonly activeSection = computed<NavSection | undefined>(
    () => this.activeEntry()?.section,
  );

  /** Les tuiles du rail : les sections du rôle, ou aucune quand le menu tient en liste plate. */
  protected readonly railSections = computed(() => (this.useRail() ? this.navGroups() : []));

  /**
   * Cible de la loupe : la liste consultable du périmètre du rôle — le parc pour qui y a accès, sa
   * propre dotation sinon. `null` masque la tuile : une loupe qui n'ouvre aucune recherche est le
   * genre de leurre que la critique UX du 10.08 a déjà sanctionné (« Filtrer » = leurre).
   */
  protected readonly searchTarget = computed(() => {
    const ids = new Set(this.nav().map((entry) => entry.id));

    return ids.has('parc') ? '/equipements' : ids.has('monmateriel') ? '/mon-materiel' : null;
  });

  /**
   * Actions de création offertes par la tuile « + ». N'y figurent que celles qui <b>ouvrent
   * réellement</b> un formulaire : un raccourci qui déposerait sur une liste, à charge pour
   * l'utilisateur d'y retrouver le bouton, promettrait plus qu'il ne tient.
   */
  protected readonly createActions = computed(() => {
    const ids = new Set(this.nav().map((entry) => entry.id));
    const actions: { label: string; path: string; query?: Record<string, string> }[] = [];

    if (ids.has('parc') && this.session.canWrite()) {
      actions.push({ label: 'Nouveau matériel', path: '/equipements', query: { nouveau: '1' } });
    }

    // UC-01 — seul le demandeur crée, et le collaborateur ne demande pas pour lui-même
    // (RM-PIN-170). Même règle que l'écran « Mes demandes », qui reste l'autorité — et la même
    // capacité : `session.canWrite()` porte l'écriture sur le parc (admin/gestion), pas sur les
    // demandes, dont le service achats dit lui-même s'il sait écrire.
    const requesterRole = this.session.role() === 'manager' || this.session.role() === 'collab';
    if (this.purchaseRequests.capabilities().canWrite && requesterRole) {
      actions.push({ label: "Nouvelle demande d'achat", path: '/demandes/nouvelle' });
    }

    return actions;
  });

  /**
   * Cloche de notifications — pour TOUS les profils : chacun a des faits qui le concernent
   * (une demande à approuver pour la comptabilité, l'avancement de sa demande pour le demandeur…).
   * La pastille compte ce qui est <b>en cours</b> — gestes à faire ET suivis d'avancement ; le détail
   * vit sur l'écran /notifications, la destination de la cloche.
   */
  protected readonly notifCount = this.notifications.pending;

  /**
   * Libellé de la cloche, adapté au rôle : « à traiter » quand un geste est attendu (IT, comptabilité),
   * « à suivre » quand il ne s'agit que de suivre l'avancement de ses demandes (demandeur). Les deux
   * ne se cumulent pas pour un même rôle, donc l'un ou l'autre suffit.
   */
  protected readonly notifLabel = computed(() => {
    const count = this.notifCount();
    if (count === 0) {
      return 'Notifications — rien à suivre';
    }

    return this.notifications.hasActionable()
      ? `Notifications — ${count} à traiter`
      : `Notifications — ${count} à suivre`;
  });

  /** Menu de création, ancré sous la tuile « + ». */
  private readonly createEl = viewChild<ElementRef<HTMLElement>>('create');
  protected readonly createOpen = signal(false);

  protected toggleCreate(): void {
    this.createOpen.update((open) => !open);
  }

  protected closeCreate(): void {
    this.createOpen.set(false);
  }

  /** Le groupe rendu par le panneau : la section ouverte, à défaut la première du rôle. */
  private readonly activeGroup = computed(
    () =>
      this.navGroups().find((group) => group.section === this.activeSection()) ??
      this.navGroups()[0],
  );

  /**
   * En-tête du panneau : le nom de la section ouverte quand le rail est en jeu, le nom du module
   * sinon — une liste plate n'est la section de rien.
   */
  protected readonly subnavHeading = computed(() =>
    this.useRail() ? (this.activeGroup()?.section ?? 'UniIT') : 'UniIT',
  );

  /** Entrées rendues par le panneau : la section ouverte, ou toutes en liste plate. */
  protected readonly subnavEntries = computed(() =>
    this.useRail() ? (this.activeGroup()?.entries ?? []) : this.nav(),
  );

  // Cible de la marque du rail : la première section visible du rôle courant — jamais un
  // chemin en dur, pour ne pas renvoyer un Collaborateur vers un écran hors de son périmètre.
  protected readonly home = computed(() => this.nav()[0]?.path ?? '/');

  /** Menu de compte (avatar) : simple fiche d'identité (nom + rôle), pas un sélecteur de profil. */
  protected toggleAccount(): void {
    this.accountOpen.update((open) => !open);
  }

  protected closeAccount(): void {
    this.accountOpen.set(false);
  }

  /** Échap ferme les deux menus du rail — celui du compte et celui de la création. */
  protected closeMenus(): void {
    this.closeAccount();
    this.closeCreate();
  }

  /** Ferme les menus du rail au clic hors de leur zone (le déclencheur et le menu). */
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;

    const account = this.accountEl()?.nativeElement;
    if (this.accountOpen() && account && !account.contains(target)) {
      this.accountOpen.set(false);
    }

    const create = this.createEl()?.nativeElement;
    if (this.createOpen() && create && !create.contains(target)) {
      this.createOpen.set(false);
    }
  }

  /**
   * Devbar (barre de bascule de profil, en haut du contenu — façon UniApp/UniScrapp). Visible en
   * démonstration (toujours) et en branché si le DevBypass expose des profils ; absent en
   * recette/production réelle, où l'identité vient du token.
   */
  protected readonly showDevbar = computed(() => this.demoMode || this.dev.enabled());

  /** Options du devbar : en démo les rôles de la maquette, en branché les profils du DevBypass. */
  protected readonly profileOptions = computed<readonly { key: string; label: string }[]>(() =>
    this.demoMode
      ? this.roles.map((role) => ({ key: role.id, label: role.label }))
      : this.dev.available().map((profile) => ({ key: profile.key, label: profile.label })),
  );

  /** Valeur courante du `<select>` du devbar. */
  protected readonly currentProfile = computed(() =>
    this.demoMode ? (this.session.role() as string) : (this.dev.current() ?? ''),
  );

  /** Bascule de profil : rôle simulé en démo (avec redirection), cookie + reload en branché. */
  protected pickProfile(key: string): void {
    if (this.demoMode) {
      this.pickRole(key as RoleId);
    } else {
      this.dev.switchTo(key);
    }
  }

  private pickRole(role: RoleId): void {
    this.session.setRole(role);

    // Le garde `canMatch` ne rejoue pas quand le rôle change : sans cette redirection,
    // un Collaborateur resterait échoué sur l'écran admin où il se trouvait.
    const allowed = navFor(role);
    const current = this.router.url.split('?')[0];
    if (!allowed.some((entry) => current === entry.path || current.startsWith(`${entry.path}/`))) {
      void this.router.navigateByUrl(allowed[0]?.path ?? '/');
    }
  }

  /**
   * Pastille de compteur du rail. Elle doit compter **ce que l'écran affichera** : un badge
   * « 6 » au-dessus d'une page « 0 matériel » fait croire à un bug ou à une perte.
   */
  private countFor(id: string): number | null {
    switch (id) {
      case 'monmateriel':
        // Même filtre que la page « Mon matériel » : le matériel au statut Assigné.
        return this.inventory.scoped().filter((asset) => asset.status === 'Assigné').length;
      case 'employes':
        return this.directory.total();
      default:
        return null;
    }
  }
}
